import React, { useState, useEffect, useCallback } from 'react';
import {
  Search, ShieldCheck, ShieldAlert, Power, X,
  ChevronLeft, ChevronRight, Mail, Phone, Loader2, Eye
} from 'lucide-react';
import PageHeader from '../../components/layout/PageHeader';
import Toast from '../../components/ui/Toast';
import { API_ENDPOINTS } from '../../config/api';
import { timeAgo, formatDateTime } from '../../lib/utils';
import RefreshButton from '../../components/ui/RefreshButton';

const ROLE_OPTIONS = ['all', 'owner', 'customer', 'driver', 'manager', 'dispatcher', 'accountant', 'admin'];

const roleBadge = (role) => {
  const map = {
    owner: 'bg-blue-100 text-blue-700',
    customer: 'bg-green-100 text-green-700',
    driver: 'bg-amber-100 text-amber-700',
    admin: 'bg-purple-100 text-purple-700',
    manager: 'bg-indigo-100 text-indigo-700',
    dispatcher: 'bg-teal-100 text-teal-700',
    accountant: 'bg-pink-100 text-pink-700'
  };
  return map[role] || 'bg-gray-100 text-gray-700';
};

const verifyBadge = (status) => {
  const map = {
    verified: 'bg-green-100 text-green-700',
    pending: 'bg-amber-100 text-amber-700',
    rejected: 'bg-red-100 text-red-700',
    suspended: 'bg-gray-200 text-gray-700'
  };
  return map[status] || 'bg-gray-100 text-gray-500';
};

const AllUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({});
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 0 });
  const [filters, setFilters] = useState({ role: 'all', status: 'all', verification: 'all', search: '' });
  const [searchInput, setSearchInput] = useState('');
  const [selected, setSelected] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [toast, setToast] = useState({ isOpen: false, title: '', message: '', type: 'info' });

  const token = () => localStorage.getItem('token');
  const notify = (title, message, type = 'info') => setToast({ isOpen: true, title, message, type });

  const fetchUsers = useCallback(async (page = pagination.page) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page,
        limit: pagination.limit,
        ...(filters.role !== 'all' && { role: filters.role }),
        ...(filters.status !== 'all' && { status: filters.status }),
        ...(filters.verification !== 'all' && { verification: filters.verification }),
        ...(filters.search && { search: filters.search })
      });
      const res = await fetch(`${API_ENDPOINTS.USERS}/admin/all-users?${params}`, {
        headers: { Authorization: `Bearer ${token()}` }
      });
      const data = await res.json();
      if (data.success) {
        setUsers(data.users);
        setPagination(data.pagination);
        setStats(data.stats || {});
      } else {
        notify('Error', data.message || 'Failed to load users', 'error');
      }
    } catch (e) {
      notify('Error', 'Failed to load users', 'error');
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.limit]); // eslint-disable-line react-hooks/exhaustive-deps

  // Refetch when filters change (reset to page 1)
  useEffect(() => {
    fetchUsers(1);
  }, [filters.role, filters.status, filters.verification, filters.search]); // eslint-disable-line react-hooks/exhaustive-deps

  const goToPage = (p) => {
    if (p < 1 || p > pagination.pages) return;
    setPagination(prev => ({ ...prev, page: p }));
    fetchUsers(p);
  };

  const submitSearch = (e) => {
    e.preventDefault();
    setFilters(prev => ({ ...prev, search: searchInput.trim() }));
  };

  // ----- Actions -----
  const callAction = async (url, method, body, okMsg) => {
    setActionLoading(true);
    try {
      const res = await fetch(url, {
        method,
        headers: { Authorization: `Bearer ${token()}`, 'Content-Type': 'application/json' },
        body: body ? JSON.stringify(body) : undefined
      });
      const data = await res.json();
      if (data.success) {
        notify('Done', data.message || okMsg, 'success');
        fetchUsers();
        setSelected(null);
        return data;
      }
      notify('Error', data.message || 'Action failed', 'error');
    } catch (e) {
      notify('Error', 'Action failed', 'error');
    } finally {
      setActionLoading(false);
    }
    return null;
  };

  const base = API_ENDPOINTS.USERS;
  const toggleActive = (u) => callAction(`${base}/admin/users/${u._id}/active`, 'PUT', { isActive: !u.isActive }, 'Status updated');
  const verifyOwner = (u) => callAction(`${base}/admin/owners/${u._id}/verify`, 'PUT', { notes: 'Verified by admin' }, 'Owner verified');
  const reactivateOwner = (u) => callAction(`${base}/admin/owners/${u._id}/reactivate`, 'PUT', {}, 'Owner reactivated');
  const rejectOrSuspend = (u, kind) => {
    const reason = window.prompt(`Reason for ${kind}:`);
    if (!reason || !reason.trim()) return;
    callAction(`${base}/admin/owners/${u._id}/${kind}`, 'PUT', { reason }, `Owner ${kind}ed`);
  };

  const StatCard = ({ label, value, accent }) => (
    <div className="bg-white rounded-xl border border-gray-200 px-4 py-3">
      <p className="text-xs text-gray-500">{label}</p>
      <p className={`text-2xl font-bold ${accent || 'text-gray-900'}`}>{value ?? 0}</p>
    </div>
  );

  return (
    <div>
      <PageHeader
        title="All Users"
        description="Customers, owners, drivers & staff — filter and manage"
        action={<RefreshButton onClick={() => fetchUsers()} loading={loading} />}
      />

      {/* Stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-5">
        <StatCard label="Total" value={stats.all} />
        <StatCard label="Owners" value={stats.owner} accent="text-blue-600" />
        <StatCard label="Customers" value={stats.customer} accent="text-green-600" />
        <StatCard label="Drivers" value={stats.driver} accent="text-amber-600" />
        <StatCard label="Staff" value={(stats.manager || 0) + (stats.dispatcher || 0) + (stats.accountant || 0)} />
        <StatCard label="Pending Owners" value={stats.pendingOwners} accent="text-amber-600" />
        <StatCard label="Inactive" value={stats.inactive} accent="text-red-600" />
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4 flex flex-wrap gap-3 items-end">
        <div>
          <label className="block text-xs text-gray-500 mb-1">Role</label>
          <select value={filters.role} onChange={(e) => setFilters(p => ({ ...p, role: e.target.value }))}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm capitalize">
            {ROLE_OPTIONS.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Status</label>
          <select value={filters.status} onChange={(e) => setFilters(p => ({ ...p, status: e.target.value }))}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm">
            <option value="all">All</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Owner verification</label>
          <select value={filters.verification} onChange={(e) => setFilters(p => ({ ...p, verification: e.target.value }))}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm">
            <option value="all">All</option>
            <option value="pending">Pending</option>
            <option value="verified">Verified</option>
            <option value="rejected">Rejected</option>
            <option value="suspended">Suspended</option>
          </select>
        </div>
        <form onSubmit={submitSearch} className="flex-1 min-w-[220px]">
          <label className="block text-xs text-gray-500 mb-1">Search</label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input value={searchInput} onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Name, email, phone, agency"
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm" />
          </div>
        </form>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
              <tr>
                <th className="text-left px-4 py-3">User</th>
                <th className="text-left px-4 py-3">Role</th>
                <th className="text-left px-4 py-3">Contact</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-left px-4 py-3">OTPs (✉ / 💬)</th>
                <th className="text-left px-4 py-3">Last used</th>
                <th className="text-left px-4 py-3">Joined</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr><td colSpan={8} className="px-4 py-10 text-center text-gray-400">
                  <Loader2 className="h-6 w-6 animate-spin mx-auto" /></td></tr>
              ) : users.length === 0 ? (
                <tr><td colSpan={8} className="px-4 py-10 text-center text-gray-400">No users found</td></tr>
              ) : users.map(u => (
                <tr key={u._id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-900">{u.name}</div>
                    {u.travelsInfo?.name && <div className="text-xs text-gray-400">{u.travelsInfo.name}</div>}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${roleBadge(u.role)}`}>{u.role}</span>
                    {u.role === 'owner' && u.verificationStatus && (
                      <span className={`ml-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${verifyBadge(u.verificationStatus)}`}>{u.verificationStatus}</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-600">
                    <div>{u.phone || '—'}</div>
                    <div className="text-xs text-gray-400">{u.email || '—'}</div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${u.isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                      {u.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-600">
                    <span title="Email OTPs">✉ {u.otpStats?.email || 0}</span>
                    <span className="mx-1 text-gray-300">|</span>
                    <span title="WhatsApp OTPs">💬 {u.otpStats?.whatsapp || 0}</span>
                  </td>
                  <td className="px-4 py-3 text-gray-500 text-xs" title={u.lastActiveAt ? formatDateTime(u.lastActiveAt) : 'Never used'}>
                    {u.lastActiveAt ? timeAgo(u.lastActiveAt) : <span className="text-gray-300">Never</span>}
                  </td>
                  <td className="px-4 py-3 text-gray-500 text-xs">{u.createdAt ? new Date(u.createdAt).toLocaleDateString('en-IN') : '—'}</td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => setSelected(u)} className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-700 text-xs font-medium">
                      <Eye className="h-4 w-4" /> View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 text-sm text-gray-600">
          <span>
            Showing {users.length ? (pagination.page - 1) * pagination.limit + 1 : 0}
            –{(pagination.page - 1) * pagination.limit + users.length} of {pagination.total}
          </span>
          <div className="flex items-center gap-1">
            <button onClick={() => goToPage(pagination.page - 1)} disabled={pagination.page <= 1}
              className="p-1.5 rounded-lg border border-gray-200 disabled:opacity-40 hover:bg-gray-50">
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="px-3">Page {pagination.page} of {pagination.pages || 1}</span>
            <button onClick={() => goToPage(pagination.page + 1)} disabled={pagination.page >= pagination.pages}
              className="p-1.5 rounded-lg border border-gray-200 disabled:opacity-40 hover:bg-gray-50">
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Detail drawer */}
      {selected && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div className="absolute inset-0 bg-black/30" onClick={() => setSelected(null)} />
          <div className="relative w-full max-w-md bg-white h-full overflow-y-auto shadow-xl p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-lg font-bold text-gray-900">{selected.name}</h3>
                <span className={`inline-block mt-1 px-2 py-0.5 rounded-full text-xs font-medium capitalize ${roleBadge(selected.role)}`}>{selected.role}</span>
              </div>
              <button onClick={() => setSelected(null)} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
            </div>

            <div className="space-y-2 text-sm text-gray-700 mb-5">
              <div className="flex items-center gap-2"><Phone className="h-4 w-4 text-gray-400" /> {selected.phone || '—'}</div>
              <div className="flex items-center gap-2"><Mail className="h-4 w-4 text-gray-400" /> {selected.email || '—'}</div>
              {selected.travelsInfo?.name && <div><span className="text-gray-400">Agency:</span> {selected.travelsInfo.name}</div>}
              {selected.ownerId?.travelsInfo?.name && <div><span className="text-gray-400">Under:</span> {selected.ownerId.travelsInfo.name}</div>}
              <div><span className="text-gray-400">Status:</span> {selected.isActive ? 'Active' : 'Inactive'}</div>
              {selected.role === 'owner' && <div><span className="text-gray-400">Verification:</span> {selected.verificationStatus || '—'}</div>}
              <div><span className="text-gray-400">Joined:</span> {selected.createdAt ? new Date(selected.createdAt).toLocaleString('en-IN') : '—'}</div>
              <div className="pt-2 border-t border-gray-100">
                <span className="text-gray-400">OTPs sent — </span>
                ✉ Email: <strong>{selected.otpStats?.email || 0}</strong>,
                💬 WhatsApp: <strong>{selected.otpStats?.whatsapp || 0}</strong>
                {selected.otpStats?.lastSentAt && <span className="text-xs text-gray-400 block mt-0.5">Last: {new Date(selected.otpStats.lastSentAt).toLocaleString('en-IN')}</span>}
              </div>
            </div>

            {/* Actions */}
            <div className="space-y-2">
              <button onClick={() => toggleActive(selected)} disabled={actionLoading}
                className="w-full flex items-center justify-center gap-2 py-2 rounded-lg border border-gray-300 hover:bg-gray-50 text-sm font-medium disabled:opacity-50">
                <Power className="h-4 w-4" /> {selected.isActive ? 'Deactivate' : 'Activate'} account
              </button>

              {selected.role === 'owner' && (
                <div className="pt-2 border-t border-gray-100 space-y-2">
                  <p className="text-xs font-semibold text-gray-500">Owner verification</p>
                  <div className="grid grid-cols-2 gap-2">
                    <button onClick={() => verifyOwner(selected)} disabled={actionLoading}
                      className="flex items-center justify-center gap-1.5 py-2 rounded-lg bg-green-600 hover:bg-green-700 text-white text-sm disabled:opacity-50">
                      <ShieldCheck className="h-4 w-4" /> Verify
                    </button>
                    <button onClick={() => rejectOrSuspend(selected, 'reject')} disabled={actionLoading}
                      className="flex items-center justify-center gap-1.5 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm disabled:opacity-50">
                      <ShieldAlert className="h-4 w-4" /> Reject
                    </button>
                    <button onClick={() => rejectOrSuspend(selected, 'suspend')} disabled={actionLoading}
                      className="flex items-center justify-center gap-1.5 py-2 rounded-lg border border-gray-300 hover:bg-gray-50 text-sm disabled:opacity-50">
                      Suspend
                    </button>
                    <button onClick={() => reactivateOwner(selected)} disabled={actionLoading}
                      className="flex items-center justify-center gap-1.5 py-2 rounded-lg border border-gray-300 hover:bg-gray-50 text-sm disabled:opacity-50">
                      Reactivate
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <Toast {...toast} onClose={() => setToast(prev => ({ ...prev, isOpen: false }))} />
    </div>
  );
};

export default AllUsers;
